import React from 'react';
import Button from '../button/index';
import { getSession, saveSession } from '../../../utils/storage';
import { getSessionInfo } from '../../../utils/getSessionInfo';
import bigDecimal from 'js-big-decimal';
import { formatAmount } from '../../../utils/formatAmount';

interface Props {
	productId: string;
	productName?: string;
	originalPrice: number;
	onApply: (price: number) => void;
	onClose: () => void;
}

const PriceMarkupContent: React.FC<Props> = ({ productId, productName, originalPrice, onApply, onClose }) => {
	const [mode, setMode] = React.useState<'amount' | 'percentage'>('amount');
	const [value, setValue] = React.useState<string>('');

	const roundTo2DecimalsStr = (bd: bigDecimal): string => {
		return Number(bd.getValue()).toFixed(2);
	};

	React.useEffect(() => {
		const { refCartId } = getSessionInfo();
		if (!refCartId) {
			setValue('');
			return;
		}

		const key = `price-overrides-${refCartId}`;
		const current = (getSession(key) as Record<string, number>) || {};
		const existing = current[productId];

		if (typeof existing === 'number') {
			const originalBD = new bigDecimal(originalPrice.toString());
			const existingBD = new bigDecimal(existing.toString());

			if (mode === 'amount') {
				setValue(roundTo2DecimalsStr(existingBD));
			} else {
				if (originalBD.compareTo(new bigDecimal('0')) !== 0) {
					const diff = existingBD.subtract(originalBD);
					const pctRaw = diff.divide(originalBD, 10).multiply(new bigDecimal('100'));
					setValue(roundTo2DecimalsStr(pctRaw));
				} else {
					setValue('');
				}
			}
		} else {
			setValue('');
		}
	}, [productId, mode, originalPrice]);

	const adjustedPriceBD = React.useMemo(() => {
		if (!value) return null;

		const valBD = new bigDecimal(value);
		if (valBD.getValue() === 'NaN') return null;

		if (mode === 'amount') {
			return valBD;
		}

		const pct = valBD.divide(new bigDecimal('100'), 10);
		const multiplier = new bigDecimal('1').add(pct);
		const priceBD = new bigDecimal(originalPrice.toString()).multiply(multiplier);

		return priceBD;
	}, [value, mode, originalPrice]);

	const adjustedPrice = adjustedPriceBD ? Number(roundTo2DecimalsStr(adjustedPriceBD)) : NaN;
	const isValid = !isNaN(adjustedPrice) && adjustedPrice > 0;

	const apply = () => {
		if (!isValid) return;

		let finalPriceBD: bigDecimal;
		if (mode === 'amount') {
			finalPriceBD = new bigDecimal(value);
		} else {
			const pct = new bigDecimal(value).divide(new bigDecimal('100'), 10);
			const multiplier = new bigDecimal('1').add(pct);
			finalPriceBD = new bigDecimal(originalPrice.toString()).multiply(multiplier);
		}

		const roundedPrice = Number(roundTo2DecimalsStr(finalPriceBD));

		const { refCartId } = getSessionInfo();
		if (refCartId) {
			const key = `price-overrides-${refCartId}`;
			const current = (getSession(key) as Record<string, number>) || {};
			current[productId] = roundedPrice;
			saveSession(key, current);
		}

		onApply(roundedPrice);
		onClose();
	};

	return (
		<div className="px-5 py-4">
			<div className="text-center">
				<h3 className="text-lg text-muted-foreground">{productName}</h3>
			</div>

			<div className="mt-2 d-flex gap-2 justify-center">
				<Button isBordered={mode !== 'amount'} onClick={() => setMode('amount')} styles="py-2 px-4">
					Amount
				</Button>

				<Button isBordered={mode !== 'percentage'} onClick={() => setMode('percentage')} styles="py-2 px-4">
					Percentage
				</Button>
			</div>

			<div className="mt-1">
				<input
					type="number"
					value={value}
					onChange={(e) => setValue(e.target.value)}
					placeholder={
						mode === 'amount' ? 'Enter new price e.g. 1200' : 'Enter percentage e.g. 10 for +10% or -5 for -5%'
					}
					className="w-100 p-2 border rounded"
				/>
			</div>

			<div className="mt-1">
				<div className="text-sm font-semibold text-muted-foreground">
					Original unit price: ₦{formatAmount(originalPrice.toFixed(2))}
				</div>
				<div className="text-sm font-semibold mt-1">
					Adjusted unit price: {isNaN(adjustedPrice) ? '—' : `₦${formatAmount(adjustedPrice.toFixed(2))}`}
				</div>
			</div>

			<div className="d-flex justify-content-end gap-2 mt-2">
				<Button isBordered={true} onClick={onClose} styles="py-2 px-4">
					Cancel
				</Button>
				<Button onClick={apply} styles="py-2 px-4" disabled={!isValid}>
					Apply
				</Button>
			</div>
		</div>
	);
};

export default PriceMarkupContent;
